import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity,
  CheckCircle,
  XCircle,
  Clock,
  Loader,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Database,
  Brain,
  Search,
  Download,
  Calendar,
  ArrowRight,
  Phone,
  Mic,
  FileText
} from 'lucide-react';
import api from '../utils/api';
import './CronMonitor.css';

const MODES = [
  { value: 'reviews', label: 'Google Reviews', icon: Search },
  { value: 'calls',   label: 'Avaya Calls',    icon: Phone },
];

const CALL_STATUS_CONFIG = {
  pending:     { label: 'Pending Transcription', icon: Clock,         color: '#6b7280', bg: 'rgba(107,114,128,0.15)' },
  transcribed: { label: 'Pending Analysis',      icon: FileText,      color: '#3b82f6', bg: 'rgba(59,130,246,0.15)' },
  analyzed:    { label: 'Analyzed',              icon: CheckCircle,   color: '#10b981', bg: 'rgba(16,185,129,0.15)' },
  skipped:     { label: 'Skipped (low quality)', icon: AlertTriangle, color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
  failed:      { label: 'Failed',                icon: XCircle,       color: '#ef4444', bg: 'rgba(239,68,68,0.15)' },
};

const msToHuman = (ms) => {
  if (!ms) return '—';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m}m`;
};

const STATUS_CONFIG = {
  pending:    { label: 'Pending',   icon: Clock,        color: '#6b7280', bg: 'rgba(107,114,128,0.15)' },
  scraping:   { label: 'Scraping',  icon: Search,       color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
  fetching:   { label: 'Fetching',  icon: Download,     color: '#3b82f6', bg: 'rgba(59,130,246,0.15)' },
  analyzing:  { label: 'Analyzing', icon: Brain,        color: '#8b5cf6', bg: 'rgba(139,92,246,0.15)' },
  completed:  { label: 'Completed', icon: CheckCircle,  color: '#10b981', bg: 'rgba(16,185,129,0.15)' },
  failed:     { label: 'Failed',    icon: XCircle,      color: '#ef4444', bg: 'rgba(239,68,68,0.15)' }
};

const PHASE_ORDER = ['pending', 'scraping', 'fetching', 'analyzing', 'completed'];

const CronMonitor = () => {
  const [mode, setMode] = useState('reviews');
  const [jobs, setJobs] = useState([]);
  const [jobsByDate, setJobsByDate] = useState({});
  const [dates, setDates] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [daysBack, setDaysBack] = useState(7);
  const [expandedDates, setExpandedDates] = useState({});
  const [expandedJobs, setExpandedJobs] = useState({});
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  // Calls-mode state
  const [callsSummary, setCallsSummary] = useState(null);
  const [callsDaysList, setCallsDaysList] = useState([]);
  const [callsFailures, setCallsFailures] = useState([]);
  const [callsInProgress, setCallsInProgress] = useState([]);

  const fetchStatus = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      if (mode === 'calls') {
        const response = await api.get(`/api/calls/pipeline-status?days=${daysBack}`);
        if (response.data.success) {
          setCallsSummary(response.data.summary || null);
          setCallsDaysList(response.data.datesList || []);
          setCallsFailures(response.data.recentFailures || []);
          setCallsInProgress(response.data.inProgress || []);
          setLastUpdated(new Date());
        }
      } else {
        const response = await api.get(`/api/services/status?days=${daysBack}`);
        if (response.data.success) {
          setJobs(response.data.jobs || []);
          setJobsByDate(response.data.jobsByDate || {});
          setDates(response.data.dates || []);
          setSummary(response.data.summary || null);
          setLastUpdated(new Date());
          // Auto-expand today's date
          if (response.data.dates && response.data.dates.length > 0) {
            setExpandedDates(prev => {
              const today = new Date().toISOString().split('T')[0];
              if (!(today in prev)) {
                return { ...prev, [today]: true };
              }
              return prev;
            });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching cron status:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [daysBack, mode]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchStatus(false), 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchStatus]);

  const toggleDate = (date) => {
    setExpandedDates(prev => ({ ...prev, [date]: !prev[date] }));
  };

  const toggleJob = (jobId) => {
    setExpandedJobs(prev => ({ ...prev, [jobId]: !prev[jobId] }));
  };

  const getPhaseProgress = (status) => {
    const idx = PHASE_ORDER.indexOf(status);
    if (idx === -1) return 0; // failed
    return ((idx + 1) / PHASE_ORDER.length) * 100;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '—';
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    const today = new Date().toISOString().split('T')[0];
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    if (dateStr === today) return 'Today';
    if (dateStr === yesterday) return 'Yesterday';
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const getDuration = (start, end) => {
    if (!start) return '—';
    const startTime = new Date(start);
    const endTime = end ? new Date(end) : new Date();
    const diffMs = endTime - startTime;
    const mins = Math.floor(diffMs / 60000);
    const secs = Math.floor((diffMs % 60000) / 1000);
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };

  const getDateSummary = (dateJobs) => {
    return {
      total: dateJobs.length,
      completed: dateJobs.filter(j => j.status === 'completed').length,
      failed: dateJobs.filter(j => j.status === 'failed').length,
      inProgress: dateJobs.filter(j => ['pending', 'scraping', 'fetching', 'analyzing'].includes(j.status)).length,
      reviewsFound: dateJobs.reduce((s, j) => s + (j.reviews_found || 0), 0),
      reviewsSaved: dateJobs.reduce((s, j) => s + (j.reviews_saved || 0), 0),
      reviewsAnalyzed: dateJobs.reduce((s, j) => s + (j.reviews_analyzed || 0), 0)
    };
  };

  const filteredJobsByDate = {};
  dates.forEach(date => {
    const dateJobs = jobsByDate[date] || [];
    const filtered = statusFilter === 'all'
      ? dateJobs
      : dateJobs.filter(j => j.status === statusFilter);
    if (filtered.length > 0) {
      filteredJobsByDate[date] = filtered;
    }
  });
  const filteredDates = dates.filter(d => filteredJobsByDate[d]);

  if (loading) {
    return (
      <div className="cron-monitor">
        <div className="cron-loading">
          <Loader size={32} className="spinning" />
          <p>Loading cron pipeline status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cron-monitor">
      {/* Header */}
      <div className="cron-header">
        <div className="cron-header-left">
          <h1 className="cron-title">
            <Activity size={28} />
            Cron Pipeline Monitor
          </h1>
          <p className="cron-subtitle">
            {mode === 'calls'
              ? 'Track Avaya call ingestion, transcription, and AI analysis'
              : 'Track daily scraping and analysis jobs'}
          </p>
        </div>
        <div className="cron-header-right">
          <div className="cron-header-controls">
            <select
              className="days-selector"
              value={mode}
              onChange={(e) => { setLoading(true); setMode(e.target.value); }}
              style={{ fontWeight: 600 }}
            >
              {MODES.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            <label className="auto-refresh-toggle">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              <span>Auto-refresh</span>
            </label>
            <select
              className="days-selector"
              value={daysBack}
              onChange={(e) => setDaysBack(parseInt(e.target.value))}
            >
              <option value={1}>Today</option>
              <option value={3}>Last 3 days</option>
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
            </select>
            <motion.button
              className="cron-refresh-btn"
              onClick={() => fetchStatus(true)}
              disabled={refreshing}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <RefreshCw size={16} className={refreshing ? 'spinning' : ''} />
              Refresh
            </motion.button>
          </div>
          {lastUpdated && (
            <span className="last-updated">
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {mode === 'calls' ? (
        <CallsView
          summary={callsSummary}
          daysList={callsDaysList}
          failures={callsFailures}
          inProgress={callsInProgress}
          expandedDates={expandedDates}
          toggleDate={toggleDate}
          formatDate={formatDate}
          formatTime={formatTime}
        />
      ) : (<>

      {/* Summary Cards */}
      {summary && (
        <div className="cron-summary-grid">
          <div className="cron-summary-card total">
            <Database size={20} />
            <div>
              <span className="summary-value">{summary.total}</span>
              <span className="summary-label">Total Jobs</span>
            </div>
          </div>
          <div className="cron-summary-card completed">
            <CheckCircle size={20} />
            <div>
              <span className="summary-value">{summary.completed}</span>
              <span className="summary-label">Completed</span>
            </div>
          </div>
          <div className="cron-summary-card failed">
            <XCircle size={20} />
            <div>
              <span className="summary-value">{summary.failed}</span>
              <span className="summary-label">Failed</span>
            </div>
          </div>
          <div className="cron-summary-card in-progress">
            <Loader size={20} />
            <div>
              <span className="summary-value">
                {summary.pending + summary.scraping + summary.fetching + summary.analyzing}
              </span>
              <span className="summary-label">In Progress</span>
            </div>
          </div>
          <div className="cron-summary-card reviews-found">
            <Search size={20} />
            <div>
              <span className="summary-value">{summary.total_reviews_found.toLocaleString()}</span>
              <span className="summary-label">Reviews Found</span>
            </div>
          </div>
          <div className="cron-summary-card reviews-saved">
            <Download size={20} />
            <div>
              <span className="summary-value">{summary.total_reviews_saved.toLocaleString()}</span>
              <span className="summary-label">Reviews Saved</span>
            </div>
          </div>
          <div className="cron-summary-card reviews-analyzed">
            <Brain size={20} />
            <div>
              <span className="summary-value">{summary.total_reviews_analyzed.toLocaleString()}</span>
              <span className="summary-label">Reviews Analyzed</span>
            </div>
          </div>
        </div>
      )}

      {/* Status Filter */}
      <div className="cron-filter-bar">
        <span className="filter-label">Filter by status:</span>
        <div className="filter-chips">
          <button
            className={`filter-chip ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All
          </button>
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
            const Icon = cfg.icon;
            const count = jobs.filter(j => j.status === key).length;
            if (count === 0) return null;
            return (
              <button
                key={key}
                className={`filter-chip ${statusFilter === key ? 'active' : ''}`}
                style={statusFilter === key ? { background: cfg.bg, borderColor: cfg.color, color: cfg.color } : {}}
                onClick={() => setStatusFilter(key)}
              >
                <Icon size={14} />
                {cfg.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Jobs by Date */}
      <div className="cron-dates-list">
        {filteredDates.length === 0 ? (
          <div className="cron-empty">
            <Calendar size={48} />
            <h3>No cron jobs found</h3>
            <p>No jobs match the current filter for the selected time period.</p>
          </div>
        ) : (
          filteredDates.map(date => {
            const dateJobs = filteredJobsByDate[date] || [];
            const dateSummary = getDateSummary(jobsByDate[date] || []);
            const isExpanded = expandedDates[date];

            return (
              <div key={date} className="cron-date-group">
                <button className="cron-date-header" onClick={() => toggleDate(date)}>
                  <div className="date-header-left">
                    <Calendar size={18} />
                    <span className="date-title">{formatDate(date)}</span>
                    <span className="date-raw">{date}</span>
                  </div>
                  <div className="date-header-stats">
                    <span className="date-stat completed">
                      <CheckCircle size={14} /> {dateSummary.completed}
                    </span>
                    {dateSummary.failed > 0 && (
                      <span className="date-stat failed">
                        <XCircle size={14} /> {dateSummary.failed}
                      </span>
                    )}
                    {dateSummary.inProgress > 0 && (
                      <span className="date-stat in-progress">
                        <Loader size={14} /> {dateSummary.inProgress}
                      </span>
                    )}
                    <span className="date-stat neutral">
                      {dateSummary.total} jobs
                    </span>
                    {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>
                </button>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      className="cron-date-jobs"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      {/* Date totals bar */}
                      <div className="date-totals-bar">
                        <span>
                          <Search size={14} /> {dateSummary.reviewsFound.toLocaleString()} found
                        </span>
                        <ArrowRight size={14} />
                        <span>
                          <Download size={14} /> {dateSummary.reviewsSaved.toLocaleString()} saved
                        </span>
                        <ArrowRight size={14} />
                        <span>
                          <Brain size={14} /> {dateSummary.reviewsAnalyzed.toLocaleString()} analyzed
                        </span>
                      </div>

                      {dateJobs.map(job => {
                        const cfg = STATUS_CONFIG[job.status] || STATUS_CONFIG.pending;
                        const StatusIcon = cfg.icon;
                        const isJobExpanded = expandedJobs[job.id];
                        const progress = getPhaseProgress(job.status);

                        return (
                          <div key={job.id} className={`cron-job-card ${job.status}`}>
                            <button className="job-card-header" onClick={() => toggleJob(job.id)}>
                              <div className="job-header-left">
                                <div className="job-status-badge" style={{ background: cfg.bg, color: cfg.color }}>
                                  <StatusIcon size={16} className={['scraping', 'fetching', 'analyzing'].includes(job.status) ? 'spinning' : ''} />
                                  {cfg.label}
                                </div>
                                <span className="job-branch-name">
                                  {job.branches?.name || 'Unknown Branch'}
                                </span>
                              </div>
                              <div className="job-header-right">
                                <div className="job-quick-stats">
                                  <span title="Reviews found">{job.reviews_found || 0} found</span>
                                  <span title="Reviews saved">{job.reviews_saved || 0} saved</span>
                                  <span title="Reviews analyzed">{job.reviews_analyzed || 0} analyzed</span>
                                </div>
                                {isJobExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                              </div>
                            </button>

                            {/* Phase progress bar */}
                            <div className="job-phase-bar">
                              <div
                                className={`job-phase-fill ${job.status}`}
                                style={{
                                  width: `${job.status === 'failed' ? 100 : progress}%`,
                                  background: job.status === 'failed'
                                    ? 'rgba(239,68,68,0.4)'
                                    : `linear-gradient(90deg, ${cfg.color}88, ${cfg.color})`
                                }}
                              />
                              <div className="phase-markers">
                                {PHASE_ORDER.map((phase, i) => (
                                  <div
                                    key={phase}
                                    className={`phase-marker ${PHASE_ORDER.indexOf(job.status) >= i || job.status === 'completed' ? 'active' : ''} ${job.status === 'failed' ? 'failed' : ''}`}
                                    title={STATUS_CONFIG[phase].label}
                                    style={{ left: `${((i + 1) / PHASE_ORDER.length) * 100}%` }}
                                  >
                                    <span className="phase-dot" />
                                    <span className="phase-name">{STATUS_CONFIG[phase].label}</span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            <AnimatePresence>
                              {isJobExpanded && (
                                <motion.div
                                  className="job-details"
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.15 }}
                                >
                                  <div className="job-details-grid">
                                    <div className="detail-item">
                                      <span className="detail-label">Started</span>
                                      <span className="detail-value">{formatTime(job.started_at)}</span>
                                    </div>
                                    <div className="detail-item">
                                      <span className="detail-label">Completed</span>
                                      <span className="detail-value">{formatTime(job.completed_at)}</span>
                                    </div>
                                    <div className="detail-item">
                                      <span className="detail-label">Duration</span>
                                      <span className="detail-value">{getDuration(job.started_at, job.completed_at)}</span>
                                    </div>
                                    <div className="detail-item">
                                      <span className="detail-label">Apify Run ID</span>
                                      <span className="detail-value mono">{job.apify_run_id || '—'}</span>
                                    </div>
                                    <div className="detail-item">
                                      <span className="detail-label">Dataset ID</span>
                                      <span className="detail-value mono">{job.dataset_id || '—'}</span>
                                    </div>
                                    <div className="detail-item">
                                      <span className="detail-label">Created</span>
                                      <span className="detail-value">{formatTime(job.created_at)}</span>
                                    </div>
                                  </div>

                                  {/* Review counts breakdown */}
                                  <div className="job-counts">
                                    <div className="count-item found">
                                      <Search size={16} />
                                      <div>
                                        <span className="count-value">{job.reviews_found || 0}</span>
                                        <span className="count-label">Found by scraper</span>
                                      </div>
                                    </div>
                                    <ArrowRight size={16} className="count-arrow" />
                                    <div className="count-item saved">
                                      <Download size={16} />
                                      <div>
                                        <span className="count-value">{job.reviews_saved || 0}</span>
                                        <span className="count-label">New reviews saved</span>
                                      </div>
                                    </div>
                                    <ArrowRight size={16} className="count-arrow" />
                                    <div className="count-item analyzed">
                                      <Brain size={16} />
                                      <div>
                                        <span className="count-value">{job.reviews_analyzed || 0}</span>
                                        <span className="count-label">AI analyzed</span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Error message if failed */}
                                  {job.error && (
                                    <div className="job-error">
                                      <AlertTriangle size={16} />
                                      <div>
                                        <strong>Error:</strong>
                                        <pre>{job.error}</pre>
                                      </div>
                                    </div>
                                  )}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })
        )}
      </div>
      </>)}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Calls mode view — simpler than reviews since calls don't have multi-phase
// per-branch jobs. Just per-day status counts + recent failures + in-progress.
// ---------------------------------------------------------------------------

const CallsView = ({
  summary, daysList, failures, inProgress,
  expandedDates, toggleDate, formatDate, formatTime,
}) => {
  if (!summary) {
    return (
      <div className="cron-empty">
        <Phone size={48} />
        <h3>No call pipeline data</h3>
        <p>No calls in the selected time range. Once the Avaya bridge uploads recordings, they'll appear here.</p>
      </div>
    );
  }

  const pct = (numerator, denominator) =>
    denominator > 0 ? `${Math.round((numerator / denominator) * 100)}%` : '0%';

  return (
    <>
      {/* Summary cards for calls */}
      <div className="cron-summary-grid">
        <div className="cron-summary-card total">
          <Phone size={20} />
          <div>
            <span className="summary-value">{summary.total.toLocaleString()}</span>
            <span className="summary-label">Total Calls</span>
          </div>
        </div>
        <div className="cron-summary-card completed">
          <CheckCircle size={20} />
          <div>
            <span className="summary-value">{summary.analyzed.toLocaleString()}</span>
            <span className="summary-label">Analyzed ({pct(summary.analyzed, summary.total)})</span>
          </div>
        </div>
        <div className="cron-summary-card in-progress">
          <Loader size={20} />
          <div>
            <span className="summary-value">
              {(summary.pending + summary.transcribed).toLocaleString()}
            </span>
            <span className="summary-label">In Pipeline</span>
          </div>
        </div>
        <div className="cron-summary-card failed">
          <XCircle size={20} />
          <div>
            <span className="summary-value">{summary.failed.toLocaleString()}</span>
            <span className="summary-label">Failed</span>
          </div>
        </div>
        <div className="cron-summary-card reviews-found">
          <Mic size={20} />
          <div>
            <span className="summary-value">{msToHuman(summary.total_duration_ms)}</span>
            <span className="summary-label">Recorded Audio</span>
          </div>
        </div>
        <div className="cron-summary-card reviews-analyzed">
          <Activity size={20} />
          <div>
            <span className="summary-value">{msToHuman(summary.total_talking_ms)}</span>
            <span className="summary-label">Talking Time</span>
          </div>
        </div>
      </div>

      {/* Per-day breakdown */}
      <div className="cron-dates-list">
        {daysList.length === 0 ? (
          <div className="cron-empty">
            <Calendar size={48} />
            <h3>No calls in this range</h3>
          </div>
        ) : (
          daysList.map(day => {
            const isExpanded = !!expandedDates[day.date];
            const pendingCount = day.pending + day.transcribed;
            return (
              <div key={day.date} className="cron-date-group">
                <button className="cron-date-header" onClick={() => toggleDate(day.date)}>
                  <div className="date-header-left">
                    <Calendar size={18} />
                    <span className="date-title">{formatDate(day.date)}</span>
                    <span className="date-raw">{day.date}</span>
                  </div>
                  <div className="date-header-stats">
                    <span className="date-stat completed">
                      <CheckCircle size={14} /> {day.analyzed}
                    </span>
                    {day.failed > 0 && (
                      <span className="date-stat failed">
                        <XCircle size={14} /> {day.failed}
                      </span>
                    )}
                    {pendingCount > 0 && (
                      <span className="date-stat in-progress">
                        <Loader size={14} /> {pendingCount}
                      </span>
                    )}
                    <span className="date-stat neutral">{day.total} calls</span>
                    {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>
                </button>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      className="cron-date-jobs"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="date-totals-bar">
                        <span>
                          <Clock size={14} /> {msToHuman(day.total_duration_ms)} audio
                        </span>
                        <ArrowRight size={14} />
                        <span>
                          <Mic size={14} /> {msToHuman(day.total_talking_ms)} talking
                        </span>
                      </div>

                      {/* Status distribution bar */}
                      <div style={{ padding: '12px 16px' }}>
                        <div style={{
                          display: 'flex', height: 24, borderRadius: 6, overflow: 'hidden',
                          background: 'rgba(107,114,128,0.15)', border: '1px solid rgba(255,255,255,0.1)'
                        }}>
                          {['analyzed', 'transcribed', 'pending', 'skipped', 'failed'].map(k => {
                            const v = day[k] || 0;
                            if (v === 0) return null;
                            const cfg = CALL_STATUS_CONFIG[k];
                            return (
                              <div
                                key={k}
                                title={`${cfg.label}: ${v}`}
                                style={{
                                  width: `${(v / day.total) * 100}%`,
                                  background: cfg.color,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: 11, fontWeight: 600, color: '#fff',
                                }}
                              >
                                {((v / day.total) * 100) > 8 ? v : ''}
                              </div>
                            );
                          })}
                        </div>
                        <div style={{
                          display: 'flex', gap: 14, marginTop: 10, flexWrap: 'wrap', fontSize: 12
                        }}>
                          {Object.entries(CALL_STATUS_CONFIG).map(([k, cfg]) => (
                            <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ width: 10, height: 10, borderRadius: '50%', background: cfg.color }} />
                              {cfg.label}: <strong>{day[k] || 0}</strong>
                            </span>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })
        )}
      </div>

      {/* In-progress list */}
      {inProgress && inProgress.length > 0 && (
        <div className="cron-date-group" style={{ marginTop: 16 }}>
          <div className="cron-date-header" style={{ cursor: 'default' }}>
            <div className="date-header-left">
              <Loader size={18} className="spinning" />
              <span className="date-title">Currently in pipeline</span>
              <span className="date-raw">{inProgress.length} call(s) waiting</span>
            </div>
          </div>
          <div className="cron-date-jobs" style={{ padding: '8px 16px 16px' }}>
            {inProgress.map(r => {
              const cfg = CALL_STATUS_CONFIG[r.status] || CALL_STATUS_CONFIG.pending;
              const Icon = cfg.icon;
              return (
                <div key={r.id} className="cron-job-card" style={{ marginBottom: 6 }}>
                  <div className="job-card-header" style={{ cursor: 'default' }}>
                    <div className="job-header-left">
                      <div className="job-status-badge" style={{ background: cfg.bg, color: cfg.color }}>
                        <Icon size={14} />
                        {cfg.label}
                      </div>
                      <span className="job-branch-name" style={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {r.recording_key}
                      </span>
                    </div>
                    <div className="job-header-right">
                      <span className="detail-value">{formatTime(r.start_time)}</span>
                      {r.retry_count > 0 && (
                        <span className="date-stat failed" style={{ marginLeft: 8 }}>
                          retry {r.retry_count}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent failures */}
      {failures && failures.length > 0 && (
        <div className="cron-date-group" style={{ marginTop: 16 }}>
          <div className="cron-date-header" style={{ cursor: 'default' }}>
            <div className="date-header-left">
              <AlertTriangle size={18} style={{ color: '#ef4444' }} />
              <span className="date-title">Recent failures</span>
              <span className="date-raw">last {failures.length}</span>
            </div>
          </div>
          <div className="cron-date-jobs" style={{ padding: '8px 16px 16px' }}>
            {failures.map(r => (
              <div key={r.id} className="cron-job-card failed" style={{ marginBottom: 6 }}>
                <div className="job-card-header" style={{ cursor: 'default' }}>
                  <div className="job-header-left">
                    <div
                      className="job-status-badge"
                      style={{ background: CALL_STATUS_CONFIG.failed.bg, color: CALL_STATUS_CONFIG.failed.color }}
                    >
                      <XCircle size={14} />
                      Failed
                    </div>
                    <span className="job-branch-name" style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {r.recording_key}
                    </span>
                  </div>
                  <div className="job-header-right">
                    <span className="detail-value">{formatTime(r.start_time)}</span>
                    <span className="date-stat failed" style={{ marginLeft: 8 }}>
                      retry {r.retry_count || 0}
                    </span>
                  </div>
                </div>
                {r.error && (
                  <div className="job-error" style={{ margin: '8px 12px 12px' }}>
                    <AlertTriangle size={14} />
                    <pre style={{ fontSize: 11, margin: 0 }}>{r.error}</pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default CronMonitor;
